'use client';

import { Eye, EyeOff } from 'lucide-react';
import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

const CONTROL =
  'w-full rounded-lg border border-line bg-surface px-3 text-sm text-ink transition placeholder:text-ink-subtle focus-ring disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-ink-subtle';

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
}

function Field({
  id,
  label,
  hint,
  error,
  required,
  className,
  children,
}: FieldProps & { id: string; children: ReactNode }) {
  return (
    <div className={className}>
      {label ? (
        <label htmlFor={id} className="field-label">
          {label}
          {required ? <span className="ml-0.5 text-red-500">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="field-hint">{hint}</p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement>, FieldProps {
  leading?: ReactNode;
  trailing?: ReactNode;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, className, wrapperClassName, leading, trailing, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  /**
   * Campo de senha mostra o que foi digitado sob demanda.
   *
   * Quem cadastra uma senha para outra pessoa precisa conferir o que digitou —
   * o erro so apareceria no primeiro acesso de quem recebeu, e a essa altura
   * ninguem sabe se foi erro de digitacao ou de leitura. O padrao continua
   * oculto, e o botao so aparece nos campos de senha.
   */
  const [revelado, setRevelado] = useState(false);
  const senha = props.type === 'password';
  const tipo = senha && revelado ? 'text' : props.type;

  const acessorio =
    trailing ??
    (senha ? (
      <button
        type="button"
        onClick={() => setRevelado((atual) => !atual)}
        aria-label={revelado ? 'Ocultar senha' : 'Mostrar senha'}
        title={revelado ? 'Ocultar senha' : 'Mostrar senha'}
        className="focus-ring pointer-events-auto rounded p-0.5 text-ink-subtle transition hover:text-ink"
        tabIndex={-1}
      >
        {revelado ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    ) : null);

  return (
    <Field
      id={inputId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={wrapperClassName}
    >
      <div className="relative">
        {leading ? (
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-subtle">
            {leading}
          </span>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={Boolean(error)}
          className={cn(
            CONTROL,
            'h-10',
            leading && 'pl-9',
            acessorio && 'pr-9',
            error && 'border-red-400 focus-visible:ring-red-400',
            className,
          )}
          {...props}
          type={tipo}
        />
        {acessorio ? (
          <span className="absolute inset-y-0 right-3 flex items-center text-ink-subtle">
            {acessorio}
          </span>
        ) : null}
      </div>
    </Field>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement>, FieldProps {
  wrapperClassName?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, className, wrapperClassName, id, children, ...props },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  return (
    <Field
      id={selectId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={wrapperClassName}
    >
      <select
        ref={ref}
        id={selectId}
        aria-invalid={Boolean(error)}
        className={cn(CONTROL, 'h-10 pr-8', error && 'border-red-400', className)}
        {...props}
      >
        {children}
      </select>
    </Field>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, FieldProps {
  wrapperClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, className, wrapperClassName, id, ...props },
  ref,
) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;

  return (
    <Field
      id={textareaId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={wrapperClassName}
    >
      <textarea
        ref={ref}
        id={textareaId}
        aria-invalid={Boolean(error)}
        className={cn(CONTROL, 'min-h-[88px] py-2.5', error && 'border-red-400', className)}
        {...props}
      />
    </Field>
  );
});

export function Checkbox({
  label,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  const generatedId = useId();
  const id = props.id ?? generatedId;

  return (
    <label
      htmlFor={id}
      className={cn('flex cursor-pointer select-none items-center gap-2.5 text-sm text-ink', className)}
    >
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded border-line text-brand focus-ring accent-[rgb(var(--brand-primary))]"
        {...props}
      />
      {label}
    </label>
  );
}
